import React, { useState } from "react";
import GridMaster from "../masters/GridMaster";
import { formatDateInDDMMYYYY } from "../../functions/common-functions";
import ActionRenderer from "../masters/ActionRenderer";
import { encryptData } from "../../utils/encryption";
import { addWorkflowEntry, AddDisposalIntimation } from "../../services/disposal-intimation-service";
import { AuthContext } from "../../contexts/AuthContext";
import { getCurrentDateTime } from "../../functions/common-functions";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import successImage from "../../icons/success-tick-logo.svg";
import errorImage from "../../icons/error-tick-logo.svg";
import { useContext } from "react";
import { addLogDetails } from "../../services/logger-entry-service";

const SentForApprovalDashboard = ({ data, resetFilter, setAction, setShowForm, setRowData, setReload, reload }) => {
  const MySwal = withReactContent(Swal);
  const context = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const handleView = (row) => {
    setRowData(row);
    setAction("View");
    setShowForm(true);
  };

  const handleEdit = (row) => {
    setRowData(row);
    setAction("Edit");
    setShowForm(true);
  };

  const handleDelete = async (row) => {
    MySwal.fire({
      title: "Delete Disposal Intimation",
      html: `
        <div style="text-align: left; margin: 1px 0;">
          <p style="margin-bottom: 15px;">Are you sure do you want to delete this disposal intimation?</p>
          <div class="g-input">
            <textarea 
              id="deleteRemarks" 
              placeholder=" " 
              maxlength="250"
            ></textarea>
            <label>Reason For Deletion <span class="req-input">*</span></label>
          </div>
          <div id="remarksError" style="color: #E1142E; font-size: 12px; margin-top: 1px; display: none;"></div>
        </div>
      `,
      confirmButtonText: "Delete",
      denyButtonText: "Cancel",
      showDenyButton: true,
      confirmButtonColor: "#810055",
      reverseButtons: true,
      customClass: "swal-confirmation delete-remark",
      preConfirm: () => {
        const remarks = document.getElementById('deleteRemarks').value.trim();
        const remarksPattern = /^[a-zA-Z0-9\s,.()]*$/;
        const errorDiv = document.getElementById('remarksError');

        // Clear previous errors
        errorDiv.style.display = 'none';

        if (!remarks) {
          errorDiv.textContent = 'Remarks are required';
          errorDiv.style.display = 'block';
          return false;
        }

        if (!remarksPattern.test(remarks)) {
          errorDiv.textContent = 'Only alphanumeric characters, space, comma, dot and brackets are allowed';
          errorDiv.style.display = 'block';
          return false;
        }

        return remarks;
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const updatedData = {
            ...row,
            Status: "Deleted",
            PendingWith: "",
            ModifiedBy: context.userId,
            ModifiedByName: context.userName,
            ModifiedOn: getCurrentDateTime("-")
          };

          const workflowData = {
            Initiator: context.userEmail,
            Action: "Delete",
            ActionBy: context.userId,
            ActionByName: context.userName,
            ActionDate: getCurrentDateTime("-"),
            Remarks: result.value,
            Status: "Deleted",
            PendingWith: ""
          };

          await addWorkflowEntry(row.IntimationNumber, workflowData);

          const response = await AddDisposalIntimation(updatedData);
          if (response.status) {
            await addLogDetails(updatedData, "Delete", row.IntimationNumber, "DisposalIntimationLog", { userName: context.userName, userId: context.userId }, "Transaction");

            MySwal.fire({
              text: "Disposal intimation deleted successfully.",
              imageUrl: successImage,
              customClass: "swal-success"
            });
            setReload(!reload);
          }
        } catch (error) {
          MySwal.fire({
            text: "Unable to delete request",
            imageUrl: errorImage,
            customClass: "swal-success"
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const columnDefs = [
    {
      headerName: "Disposal Intimation Number",
      field: "IntimationNumber",
      minWidth: 180,
      tooltipField: "IntimationNumber",
      sort: 'asc'
    },
    {
      headerName: "Date",
      field: "IntimationDate",
      minWidth: 100,
      tooltipField: "IntimationDate",
      valueFormatter: (params) => {
        return params.value ? formatDateInDDMMYYYY(params.value.toString()) : "";
      },
      filter: "agDateColumnFilter",
      filterParams: {
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          if (cellValue == null) return 0;
          const dateCell = new Date(cellValue);
          dateCell.setHours(0, 0, 0, 0);
          if (dateCell < filterLocalDateAtMidnight) return -1;
          if (dateCell > filterLocalDateAtMidnight) return 1;
          return 0;
        },
      },
    },
    {
      headerName: "Initiator Name",
      field: "InitiatorName",
      minWidth: 150,
      tooltipField: "InitiatorName",
    },
    {
      headerName: "Division",
      field: "InitiatorDivision",
      minWidth: 120,
      tooltipField: "InitiatorDivision",
    },
    {
      headerName: "Location",
      field: "InitiatorLocation",
      minWidth: 120,
      valueGetter: (params) => {
        return params.data.InitiatorLocation === "Other" && params.data.IfOtherLoc
          ? params.data.IfOtherLoc
          : params.data.InitiatorLocation || "";
      },
      tooltipValueGetter: (params) => {
        return params.data.InitiatorLocation === "Other" && params.data.IfOtherLoc
          ? params.data.IfOtherLoc
          : params.data.InitiatorLocation || "";
      }
    },
    {
      headerName: "Item Code",
      field: "ItemCode",
      minWidth: 120,
      tooltipField: "ItemCode",
    },
    {
      headerName: "Item Description",
      field: "ItemDescription",
      minWidth: 150,
      tooltipField: "ItemDescription",
    },
    {
      headerName: "Status",
      field: "Status",
      minWidth: 180,
      tooltipField: "Status",
    },
    {
      headerName: "Pending with",
      field: "PendingWith",
      minWidth: 150,
      tooltipValueGetter: (params) => {
        if (Array.isArray(params.value)) {
          const decryptedNames = params.value.map(name => encryptData(name, "dec"));
          return decryptedNames.join(", ");
        }
        if (params.value && params.value.includes(',')) {
          const names = params.value.split(',').map(name => encryptData(name.trim(), "dec"));
          return names.join(", ");
        }
        return params.value ? encryptData(params.value, "dec") : "";
      },
      valueFormatter: (params) => {
        if (Array.isArray(params.value)) {
          const decryptedNames = params.value.map(name => encryptData(name, "dec"));
          return decryptedNames.join(", ");
        }
        if (params.value && params.value.includes(',')) {
          const names = params.value.split(',').map(name => encryptData(name.trim(), "dec"));
          return names.join(", ");
        }
        return params.value ? encryptData(params.value, "dec") : "";
      }
    },
    {
      headerName: "",
      field: "",
      maxWidth: 100,
      cellRenderer: (params) => {
        const isRework = params.data.Status === "Rework";
        return (
          <ActionRenderer
            selectedData={params.data}
            handleViewEvent={() => handleView(params.data)}
            handleEditEvent={isRework ? () => handleEdit(params.data) : null}
            handleDeleteEvent={isRework ? () => handleDelete(params.data) : null}
            showView={true}
            showEdit={isRework}
            showDelete={isRework}
          />
        );
      },
      sortable: false,
      filter: false,
      pinned: "right",
      resizable: false,
    }
  ];

  const headerName = (field) => {
    const mapping = {
      "IntimationNumber": "Disposal Intimation Number",
      "IntimationDate": "Date",
      "InitiatorName": "Initiator Name",
      "InitiatorDivision": "Division",
      "InitiatorLocation": "Location",
      "ItemCode": "Item Code",
      "ItemDescription": "Item Description",
      "Status": "Status",
      "PendingWith": "Pending with"
    };
    return mapping[field] || "No match found";
  };

  const fieldName = (header) => {
    const mapping = {
      "Disposal Intimation Number": "IntimationNumber",
      "Date": "IntimationDate",
      "Initiator Name": "InitiatorName",
      "Division": "InitiatorDivision",
      "Location": "InitiatorLocation",
      "Item Code": "ItemCode",
      "Item Description": "ItemDescription",
      "Status": "Status",
      "Pending with": "PendingWith"
    };
    return mapping[header] || "No match found";
  };

  return (
    <div className="dashboard-panel">
      <h5 className="panel-title"><span>&nbsp;&nbsp;&nbsp;</span>Requests Sent for Approval</h5>
      <GridMaster
        rowDetails={data}
        colDetails={columnDefs}
        fieldNames={fieldName}
        headerNames={headerName}
        getDataEvent={() => { }}
        getFilterColLength={() => { }}
        setExcelData={() => { }}
        resetind={resetFilter}
      />
      <div><span>&nbsp;&nbsp;&nbsp;</span><b>** Only pending requests are visible. Deleted and Rejected are not shown.</b></div>
    </div>
  );
};

export default SentForApprovalDashboard;
